import React, { useState, useEffect, useCallback } from 'react';
import { quizService } from '../../../services/quiz';
import { authService } from '../../../services/auth';
import { useAuth } from '../../../context/AuthContext';
import { useMasterEvent } from '../../../context/MasterEventContext';
import Avatar from '../../shared/Avatar';
import './QuizActivity.css';

// ===== Theme & Question Setup =====
function QuizSetup({ quiz, activityId, masterEventId, members, onRefresh }) {
  const [customQ, setCustomQ] = useState('');
  const [customA, setCustomA] = useState('');
  const [customTheme, setCustomTheme] = useState('');
  const [customPts, setCustomPts] = useState('1');
  const [selectedThemeForSuggestions, setSelectedThemeForSuggestions] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const availableThemes = quizService.getAvailableThemes();
  const difficulties = quizService.getDifficulties();
  const difficultyLabels = { beginner: '🟢 Beginner', intermediate: '🟡 Intermediate', pro: '🔴 Pro' };

  const handleAddTheme = (theme) => {
    quizService.addTheme(activityId, theme);
    onRefresh();
  };

  const handleRemoveTheme = (theme) => {
    quizService.removeTheme(activityId, theme);
    onRefresh();
  };

  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const fetchSuggestions = async (theme, diff) => {
    setLoadingSuggestions(true);
    try {
      const results = await quizService.fetchSuggestedQuestions(theme, 5, diff);
      setSuggestions(results);
    } catch {
      setSuggestions(quizService.getSuggestedQuestions(theme, 5, diff));
    }
    setLoadingSuggestions(false);
  };

  const handleShowSuggestions = (theme, diff) => {
    setSelectedThemeForSuggestions(theme);
    const d = diff !== undefined ? diff : selectedDifficulty;
    fetchSuggestions(theme, d);
  };

  const handleDifficultyChange = (diff) => {
    const newDiff = selectedDifficulty === diff ? null : diff;
    setSelectedDifficulty(newDiff);
    if (selectedThemeForSuggestions) {
      fetchSuggestions(selectedThemeForSuggestions, newDiff);
    }
  };

  const handleRefreshSuggestions = () => {
    if (selectedThemeForSuggestions) {
      fetchSuggestions(selectedThemeForSuggestions, selectedDifficulty);
    }
  };

  const handleAddSuggestion = (s) => {
    quizService.addQuestion(activityId, { question: s.q, answer: s.a, theme: selectedThemeForSuggestions, points: 1 });
    setSuggestions((prev) => prev.filter((x) => x.q !== s.q));
    onRefresh();
  };

  const handleAddCustom = () => {
    if (!customQ.trim()) return;
    quizService.addQuestion(activityId, {
      question: customQ.trim(),
      answer: customA.trim(),
      theme: customTheme.trim() || 'Custom',
      points: parseInt(customPts, 10) || 1,
    });
    setCustomQ(''); setCustomA(''); setCustomTheme(''); setCustomPts('1');
    onRefresh();
  };

  const handleRemoveQuestion = (qId) => {
    quizService.removeQuestion(activityId, qId);
    onRefresh();
  };

  const handleStart = () => {
    quizService.startQuiz(activityId);
    onRefresh();
  };

  return (
    <div className="quiz-setup">
      <h3 className="quiz-section-title">Themes</h3>
      <div className="quiz-themes-grid">
        {availableThemes.map((t) => {
          const isAdded = quiz.themes.includes(t);
          return (
            <button key={t} className={`quiz-theme-btn ${isAdded ? 'quiz-theme-btn--active' : ''}`}
              onClick={() => isAdded ? handleRemoveTheme(t) : handleAddTheme(t)}>
              {t} {isAdded ? '✓' : '+'}
            </button>
          );
        })}
      </div>

      {/* Suggested questions from selected theme */}
      {quiz.themes.length > 0 && (
        <div className="quiz-suggest-section">
          <h4 className="quiz-section-subtitle">Get Suggested Questions</h4>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {quiz.themes.map((t) => (
              <button key={t} className={`quiz-theme-pill ${selectedThemeForSuggestions === t ? 'quiz-theme-pill--active' : ''}`}
                onClick={() => handleShowSuggestions(t)}>
                {t}
              </button>
            ))}
          </div>
          {/* Difficulty filter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Difficulty:</span>
            {difficulties.map((d) => (
              <button key={d} className={`quiz-diff-btn ${selectedDifficulty === d ? 'quiz-diff-btn--active' : ''}`}
                onClick={() => handleDifficultyChange(d)}
                style={{
                  padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                  background: selectedDifficulty === d ? (d === 'beginner' ? 'rgba(34,197,94,0.15)' : d === 'intermediate' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)') : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${selectedDifficulty === d ? (d === 'beginner' ? 'rgba(34,197,94,0.3)' : d === 'intermediate' ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)') : 'rgba(255,255,255,0.08)'}`,
                  color: selectedDifficulty === d ? (d === 'beginner' ? '#22c55e' : d === 'intermediate' ? '#fbbf24' : '#f87171') : 'rgba(255,255,255,0.6)',
                }}>
                {difficultyLabels[d]}
              </button>
            ))}
            {selectedDifficulty && (
              <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer' }}
                onClick={() => handleDifficultyChange(selectedDifficulty)}>Clear</button>
            )}
          </div>
          {loadingSuggestions && (
            <div style={{ padding: '16px 0', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Fetching questions...
            </div>
          )}
          {!loadingSuggestions && suggestions.length > 0 && (
            <div className="quiz-suggestions">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {selectedThemeForSuggestions}{selectedDifficulty ? ` (${selectedDifficulty})` : ''}
                  {suggestions[0]?.source === 'opentdb' && <span style={{ marginLeft: 6, fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>via OpenTDB</span>}
                </span>
                <button className="quiz-btn quiz-btn--small" onClick={handleRefreshSuggestions}>🔄 New questions</button>
              </div>
              {suggestions.map((s, i) => (
                <div key={i} className="quiz-suggestion-item">
                  <div className="quiz-suggestion-q">{s.q}</div>
                  <div className="quiz-suggestion-a">
                    Answer: {s.a}
                    {s.d && <span style={{
                      marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                      background: s.d === 'beginner' ? 'rgba(34,197,94,0.12)' : s.d === 'intermediate' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                      color: s.d === 'beginner' ? '#22c55e' : s.d === 'intermediate' ? '#fbbf24' : '#f87171',
                    }}>{s.d}</span>}
                  </div>
                  <button className="quiz-btn quiz-btn--small quiz-btn--green" onClick={() => handleAddSuggestion(s)}>+ Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom question */}
      <div className="quiz-custom-section">
        <h4 className="quiz-section-subtitle">Add Custom Question</h4>
        <div className="quiz-custom-form">
          <input type="text" value={customQ} onChange={(e) => setCustomQ(e.target.value)} placeholder="Question..." className="quiz-input" />
          <input type="text" value={customA} onChange={(e) => setCustomA(e.target.value)} placeholder="Answer (for quizmaster)" className="quiz-input" />
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="text" value={customTheme} onChange={(e) => setCustomTheme(e.target.value)} placeholder="Theme (optional)" className="quiz-input" style={{ flex: 1 }} />
            <input type="number" value={customPts} onChange={(e) => setCustomPts(e.target.value)} min="1" max="10" className="quiz-input" style={{ width: 60 }} />
            <button className="quiz-btn quiz-btn--green" onClick={handleAddCustom} disabled={!customQ.trim()}>Add</button>
          </div>
        </div>
      </div>

      {/* Question list */}
      {quiz.questions.length > 0 && (
        <div className="quiz-question-list">
          <h4 className="quiz-section-subtitle">Questions ({quiz.questions.length})</h4>
          {quiz.questions.map((q, idx) => (
            <div key={q.id} className="quiz-q-item">
              <span className="quiz-q-num">{idx + 1}</span>
              <div className="quiz-q-content">
                <div className="quiz-q-text">{q.question}</div>
                <div className="quiz-q-meta">{q.theme} — {q.points} pt{q.points !== 1 ? 's' : ''}</div>
              </div>
              <button className="quiz-btn quiz-btn--small quiz-btn--danger" onClick={() => handleRemoveQuestion(q.id)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {quiz.questions.length > 0 && (
        <button className="quiz-btn quiz-btn--primary quiz-btn--large" onClick={handleStart}>
          Start Quiz ({quiz.questions.length} questions)
        </button>
      )}
    </div>
  );
}

// ===== Member Management during Quiz =====
function QuizMemberManager({ quiz, activityId, members, allUsers, onRefresh, masterEventId }) {
  const [showManage, setShowManage] = useState(false);
  const [addUserId, setAddUserId] = useState('');

  // Users in scoreboard (active participants)
  const participantIds = new Set([
    ...members.flatMap((m) => [m.userId, m.user?.id, m.user?.userId, m.user?.cognitoId].filter(Boolean)),
    ...Object.keys(quiz.scoreboard),
  ]);

  // Users not yet participating
  const nonParticipants = allUsers.filter((u) => !participantIds.has(u.id) && u.enabled !== false);

  const handleAddParticipant = async () => {
    if (!addUserId) return;
    // Also add to the master event as a member if not already
    const { masterEventService } = require('../../../services/masterEvent');
    try { await masterEventService.addMember(masterEventId, addUserId, 'participant'); } catch (e) { /* already a member */ }
    // Initialize them in the scoreboard with 0 points so they appear
    if (!quiz.scoreboard[addUserId]) {
      quizService.adjustPoints(activityId, addUserId, 0, 'Joined mid-quiz');
    }
    setAddUserId('');
    await onRefresh();
  };

  const handleRemoveParticipant = (userId) => {
    if (!window.confirm('Remove this participant from the quiz scoreboard?')) return;
    const quizData = quizService.getQuiz(activityId);
    if (quizData && quizData.scoreboard[userId]) {
      delete quizData.scoreboard[userId];
      quizService.updateQuiz(activityId, { scoreboard: quizData.scoreboard });
      onRefresh();
    }
  };

  const scoreboard = quizService.getScoreboard(activityId);

  return (
    <div className="quiz-adjust-section" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowManage(!showManage)}>
        <h4 className="quiz-section-subtitle" style={{ margin: 0 }}>Manage Participants</h4>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{showManage ? '▼' : '▶'} {scoreboard.length} players</span>
      </div>
      {showManage && (
        <div style={{ marginTop: 10 }}>
          {/* Add participant */}
          {nonParticipants.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)} className="quiz-input" style={{ flex: 1 }}>
                <option value="">Add a participant...</option>
                {nonParticipants.map((u) => <option key={u.id} value={u.id}>{u.avatar} {u.displayName}</option>)}
              </select>
              <button className="quiz-btn quiz-btn--green" onClick={handleAddParticipant} disabled={!addUserId}>+ Add</button>
            </div>
          )}
          {/* Current participants */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {scoreboard.map((entry) => {
              const u = allUsers.find((x) => x.id === entry.userId);
              return (
                <div key={entry.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                  <Avatar user={u} size={16} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{u?.displayName || 'Unknown'}</span>
                  <span style={{ fontSize: 12, color: '#a855f7', fontWeight: 700 }}>{entry.points} pts</span>
                  {entry.userId !== quiz.quizmasterId && (
                    <button className="quiz-btn quiz-btn--small quiz-btn--danger" onClick={() => handleRemoveParticipant(entry.userId)}>✕</button>
                  )}
                </div>
              );
            })}
            {/* Members not yet in scoreboard */}
            {members.filter((m) => !quiz.scoreboard[m.userId]).map((m) => {
              const u = allUsers.find((x) => x.id === m.userId);
              return (
                <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', opacity: 0.5 }}>
                  <Avatar user={u} size={16} />
                  <span style={{ flex: 1, fontSize: 13 }}>{u?.displayName || 'Unknown'}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>not scored yet</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Active Quiz (Quizmaster View) =====
function QuizmasterView({ quiz, activityId, members, allUsers, onRefresh }) {
  const currentQ = quiz.questions[quiz.currentQuestionIdx];
  const [awardPts, setAwardPts] = useState(String(currentQ?.points || 1));
  const [adjustUserId, setAdjustUserId] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [newQmId, setNewQmId] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [liveQ, setLiveQ] = useState('');
  const [liveA, setLiveA] = useState('');
  const [livePts, setLivePts] = useState('1');

  const pendingQuestions = quiz.questions.filter((q) => q.status === 'pending');
  const answeredQuestions = quiz.questions.filter((q) => q.status === 'answered');

  const handleActivate = (idx) => {
    quizService.activateQuestion(activityId, idx);
    onRefresh();
  };

  const handleAward = (userId) => {
    if (!currentQ) return;
    quizService.awardQuestion(activityId, currentQ.id, userId, parseFloat(awardPts) || currentQ.points);
    const nextIdx = quiz.questions.findIndex((q, i) => i > quiz.currentQuestionIdx && q.status === 'pending');
    if (nextIdx >= 0) quizService.activateQuestion(activityId, nextIdx);
    setShowAnswer(false);
    onRefresh();
    setAwardPts(String(quiz.questions[nextIdx]?.points || 1));
  };

  const handleSkip = () => {
    if (!currentQ) return;
    quizService.skipQuestion(activityId, currentQ.id);
    const nextIdx = quiz.questions.findIndex((q, i) => i > quiz.currentQuestionIdx && q.status === 'pending');
    if (nextIdx >= 0) quizService.activateQuestion(activityId, nextIdx);
    setShowAnswer(false);
    onRefresh();
  };

  const handleAdjust = () => {
    if (!adjustUserId || !adjustAmount) return;
    quizService.adjustPoints(activityId, adjustUserId, parseFloat(adjustAmount), adjustReason.trim() || 'Adjustment');
    setAdjustUserId(''); setAdjustAmount(''); setAdjustReason('');
    onRefresh();
  };

  const handleChangeQm = () => {
    if (!newQmId) return;
    quizService.changeQuizmaster(activityId, newQmId);
    setNewQmId('');
    onRefresh();
  };

  const handleFinish = async () => {
    if (!window.confirm('Finish the quiz? Scores will be pushed to the master event.')) return;
    try { await quizService.finishQuiz(activityId); await onRefresh(); }
    catch (err) { window.alert(err.message || 'Could not save quiz scores'); }
  };

  const scoreboard = quizService.getScoreboard(activityId);

  // Merged participant list for award buttons
  const allParticipantIds = [...new Set([...members.map((m) => m.userId), ...Object.keys(quiz.scoreboard)])];

  return (
    <div className="quiz-master-layout">
      {/* Left sidebar */}
      <div className="quiz-sidebar">
        <div className="quiz-sidebar-header">
          <span className="quiz-qm-badge">🎙️ Quizmaster</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{answeredQuestions.length}/{quiz.questions.length}</span>
        </div>

        <Scoreboard scoreboard={scoreboard} allUsers={allUsers} />

        {/* Point Adjustments */}
        <div className="quiz-sidebar-section">
          <h4 className="quiz-sidebar-title">Point Adjustments</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <select value={adjustUserId} onChange={(e) => setAdjustUserId(e.target.value)} className="quiz-input" style={{ fontSize: 12 }}>
              <option value="">Player...</option>
              {allParticipantIds.map((uid) => {
                const u = allUsers.find((x) => x.id === uid);
                return <option key={uid} value={uid}>{u?.displayName}</option>;
              })}
            </select>
            <div style={{ display: 'flex', gap: 4 }}>
              <input type="number" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder="±" className="quiz-input" style={{ width: 50, fontSize: 12 }} />
              <input type="text" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="Reason" className="quiz-input" style={{ flex: 1, fontSize: 12 }} />
            </div>
            <button className="quiz-btn quiz-btn--small quiz-btn--secondary" onClick={handleAdjust} disabled={!adjustUserId || !adjustAmount}>Apply</button>
          </div>
        </div>

        {/* Manage Participants */}
        <QuizMemberManager quiz={quiz} activityId={activityId} masterEventId={quiz.masterEventId} members={members} allUsers={allUsers} onRefresh={onRefresh} />

        {/* Change Quizmaster */}
        <div className="quiz-sidebar-section">
          <h4 className="quiz-sidebar-title">Quizmaster</h4>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={newQmId} onChange={(e) => setNewQmId(e.target.value)} className="quiz-input" style={{ flex: 1, fontSize: 12 }}>
              <option value="">Hand over to...</option>
              {members.filter((m) => m.userId !== quiz.quizmasterId).map((m) => {
                const u = allUsers.find((x) => x.id === m.userId);
                return <option key={m.userId} value={m.userId}>{u?.displayName}</option>;
              })}
            </select>
            <button className="quiz-btn quiz-btn--small quiz-btn--secondary" onClick={handleChangeQm} disabled={!newQmId}>Go</button>
          </div>
        </div>

        {pendingQuestions.length === 0 && quiz.questions.some((q) => q.status === 'answered') && (
          <button className="quiz-btn quiz-btn--primary" onClick={handleFinish} style={{ width: '100%', marginTop: 12, fontSize: 12, padding: '10px 12px' }}>
            Finish Quiz
          </button>
        )}
      </div>

      {/* Main area */}
      <div className="quiz-main-area">
        {/* Current question */}
        {currentQ && currentQ.status === 'active' ? (
          <div className="quiz-current-q">
            <div className="quiz-current-q-num">Question {quiz.currentQuestionIdx + 1}</div>
            <div className="quiz-current-q-theme">{currentQ.theme}</div>
            <h3 className="quiz-current-q-text">{currentQ.question}</h3>
            <div className="quiz-current-q-answer" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="quiz-answer-label">Answer:</span>
              {showAnswer ? (
                <span>{currentQ.answer || 'No answer set'}</span>
              ) : (
                <span style={{ letterSpacing: 2, color: 'rgba(255,255,255,0.3)' }}>{'•'.repeat(Math.max((currentQ.answer || '').length, 6))}</span>
              )}
              <button
                onClick={() => setShowAnswer(!showAnswer)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
                  marginLeft: 'auto', padding: '2px 6px', opacity: 0.7, transition: 'opacity 0.15s',
                }}
                title={showAnswer ? 'Hide answer' : 'Reveal answer'}
              >
                {showAnswer ? '🙈' : '👁️'}
              </button>
            </div>
            <div className="quiz-current-q-points">
              <label>Points:</label>
              <input type="number" value={awardPts} onChange={(e) => setAwardPts(e.target.value)} min="0" max="10" step="0.5" className="quiz-input quiz-input--small" />
            </div>
            <div className="quiz-award-grid">
              <span className="quiz-award-label">Who answered correctly?</span>
              {allParticipantIds.map((uid) => {
                const u = allUsers.find((x) => x.id === uid);
                return (
                  <button key={uid} className="quiz-award-btn" onClick={() => handleAward(uid)}>
                    <Avatar user={u} size={18} className="quiz-award-avatar" />
                    <span className="quiz-award-name">{u?.displayName || 'Unknown'}</span>
                  </button>
                );
              })}
            </div>
            <button className="quiz-btn quiz-btn--secondary" onClick={handleSkip} style={{ marginTop: 10 }}>Skip (no winner)</button>
          </div>
        ) : pendingQuestions.length > 0 ? (
          <div className="quiz-next-prompt">
            <p>Select the next question:</p>
            <div className="quiz-pending-list">
              {quiz.questions.map((q, idx) => q.status === 'pending' ? (
                <button key={q.id} className="quiz-pending-btn" onClick={() => handleActivate(idx)}>
                  <span className="quiz-q-num">{idx + 1}</span>
                  <span>{q.question}</span>
                  <span className="quiz-q-meta">{q.theme}</span>
                </button>
              ) : null)}
            </div>
          </div>
        ) : (
          <div className="quiz-all-done">
            <h3>All questions answered!</h3>
            <button className="quiz-btn quiz-btn--primary quiz-btn--large" onClick={handleFinish}>Finish Quiz & Push Scores</button>
          </div>
        )}

        {/* Answered questions log + add question at bottom */}
        {answeredQuestions.length > 0 && (
          <div className="quiz-answered-log" style={{ marginTop: 20 }}>
            <h4 className="quiz-section-subtitle">Answered</h4>
            {quiz.questions.filter((q) => q.status === 'answered').map((q) => {
              const winner = q.winnerId ? allUsers.find((u) => u.id === q.winnerId) : null;
              return (
                <div key={q.id} className="quiz-answered-item">
                  <span className="quiz-q-num">{quiz.questions.indexOf(q) + 1}</span>
                  <div>
                    <div className="quiz-q-text">{q.question}</div>
                    <div className="quiz-q-meta">
                      {q.winnerId ? <><span style={{ color: '#22c55e' }}>{winner?.displayName}</span> +{q.awardedPoints}</> : <span style={{ color: 'rgba(255,255,255,0.4)' }}>Skipped</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* + Add question at the end */}
        <div className="quiz-add-inline" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="text" value={liveQ} onChange={(e) => setLiveQ(e.target.value)} placeholder="+ Add another question..." className="quiz-input" style={{ flex: 1 }} />
            <input type="text" value={liveA} onChange={(e) => setLiveA(e.target.value)} placeholder="Answer" className="quiz-input" style={{ width: 120 }} />
            <input type="number" value={livePts} onChange={(e) => setLivePts(e.target.value)} min="1" max="10" className="quiz-input" style={{ width: 50 }} />
            <button className="quiz-btn quiz-btn--green" disabled={!liveQ.trim()} onClick={() => {
              quizService.addQuestion(activityId, { question: liveQ.trim(), answer: liveA.trim(), theme: 'Live', points: parseInt(livePts, 10) || 1 });
              setLiveQ(''); setLiveA(''); setLivePts('1');
              onRefresh();
            }}>+</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Participant View =====
function ParticipantView({ quiz, allUsers }) {
  const currentQ = quiz.questions[quiz.currentQuestionIdx];
  const scoreboard = quizService.getScoreboard(quiz.activityId);
  const answeredCount = quiz.questions.filter((q) => q.status === 'answered').length;

  return (
    <div className="quiz-participant-view">
      <div className="quiz-qm-header">
        <span className="quiz-qm-progress">{answeredCount}/{quiz.questions.length} answered</span>
      </div>

      {currentQ && currentQ.status === 'active' ? (
        <div className="quiz-current-q quiz-current-q--participant">
          <div className="quiz-current-q-num">Question {quiz.currentQuestionIdx + 1}</div>
          <div className="quiz-current-q-theme">{currentQ.theme}</div>
          <h3 className="quiz-current-q-text">{currentQ.question}</h3>
          <div className="quiz-current-q-points">{currentQ.points} point{currentQ.points !== 1 ? 's' : ''}</div>
        </div>
      ) : (
        <div className="quiz-waiting">
          <span style={{ fontSize: 32 }}>🧠</span>
          <p>Waiting for the quizmaster to ask the next question...</p>
        </div>
      )}

      <Scoreboard scoreboard={scoreboard} allUsers={allUsers} />

      {/* Answered questions log */}
      <div className="quiz-answered-log">
        <h4 className="quiz-section-subtitle">Questions So Far</h4>
        {quiz.questions.filter((q) => q.status === 'answered').map((q, idx) => {
          const winner = q.winnerId ? allUsers.find((u) => u.id === q.winnerId) : null;
          return (
            <div key={q.id} className="quiz-answered-item">
              <span className="quiz-q-num">{quiz.questions.indexOf(q) + 1}</span>
              <div>
                <div className="quiz-q-text">{q.question}</div>
                <div className="quiz-q-meta">
                  {q.winnerId ? (
                    <><span style={{ color: '#22c55e' }}>{winner?.displayName || 'Unknown'}</span> won {q.awardedPoints} pt{q.awardedPoints !== 1 ? 's' : ''}</>
                  ) : (
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>Skipped</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Scoreboard =====
function Scoreboard({ scoreboard, allUsers }) {
  if (scoreboard.length === 0) return null;
  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="quiz-scoreboard">
      <h4 className="quiz-section-subtitle">Scoreboard</h4>
      <div className="quiz-scoreboard-list">
        {scoreboard.map((entry, idx) => {
          const u = allUsers.find((x) => x.id === entry.userId);
          return (
            <div key={entry.userId} className={`quiz-score-row ${idx === 0 ? 'quiz-score-row--first' : ''}`}>
              <span className="quiz-score-rank">{medals[idx] || `#${idx + 1}`}</span>
              <Avatar user={u} size={18} className="quiz-score-avatar" />
              <span className="quiz-score-name">{u?.displayName || 'Unknown'}</span>
              <span className="quiz-score-pts">{entry.points} pts</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Finished View =====
function FinishedView({ quiz, allUsers }) {
  const scoreboard = quizService.getScoreboard(quiz.activityId);
  return (
    <div className="quiz-finished">
      <div className="quiz-finished-header">
        <span style={{ fontSize: 48 }}>🏆</span>
        <h2>Quiz Complete!</h2>
        <p>{quiz.questions.length} questions played</p>
      </div>
      <Scoreboard scoreboard={scoreboard} allUsers={allUsers} />
      <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 16 }}>
        Scores have been pushed to the master event leaderboard.
      </p>
    </div>
  );
}

// ===== Main Quiz Component =====
export default function QuizActivity({ activity }) {
  const { user } = useAuth();
  const { members, masterEvent } = useMasterEvent();
  const allUsers = authService.getAllUsers();
  const [quiz, setQuiz] = useState(null);

  const loadQuiz = useCallback(() => {
    let q = quizService.getQuiz(activity.id);
    if (!q) {
      q = quizService.createQuiz({
        activityId: activity.id,
        masterEventId: activity.masterEventId,
        quizmasterId: user?.id || masterEvent?.createdBy,
      });
    }
    setQuiz(q);
  }, [activity.id, activity.masterEventId, user?.id, masterEvent?.createdBy]);

  useEffect(() => { loadQuiz(); }, [loadQuiz]);
  // Poll for updates every 3 seconds during active quiz
  useEffect(() => {
    if (quiz?.status === 'active') {
      const i = setInterval(loadQuiz, 3000);
      return () => clearInterval(i);
    }
  }, [quiz?.status, loadQuiz]);

  if (!quiz) return <div className="quiz-loading">Loading quiz...</div>;

  const isQuizmaster = user?.id === quiz.quizmasterId;
  const qmUser = allUsers.find((u) => u.id === quiz.quizmasterId);

  return (
    <div className="quiz-activity">
      <div className="quiz-header">
        <div>
          <h3 className="quiz-title">{activity.name}</h3>
          {activity.description && <p className="quiz-desc">{activity.description}</p>}
        </div>
        <div className="quiz-qm-info">
          <span className="quiz-qm-label">Quizmaster</span>
          <span className="quiz-qm-name"><Avatar user={qmUser} size={18} style={{display:'inline-block',verticalAlign:'middle',marginRight:4}} />{qmUser?.displayName}</span>
        </div>
      </div>

      {quiz.status === 'setup' && isQuizmaster && (
        <QuizSetup quiz={quiz} activityId={activity.id} masterEventId={activity.masterEventId} members={members} onRefresh={loadQuiz} />
      )}
      {quiz.status === 'setup' && !isQuizmaster && (
        <div className="quiz-waiting">
          <span style={{ fontSize: 32 }}>🧠</span>
          <p>The quizmaster is setting up the quiz. Hang tight!</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{quiz.questions.length} questions prepared so far</p>
        </div>
      )}
      {quiz.status === 'active' && isQuizmaster && (
        <QuizmasterView quiz={quiz} activityId={activity.id} members={members} allUsers={allUsers} onRefresh={loadQuiz} />
      )}
      {quiz.status === 'active' && !isQuizmaster && (
        <ParticipantView quiz={quiz} allUsers={allUsers} />
      )}
      {quiz.status === 'finished' && (
        <FinishedView quiz={quiz} allUsers={allUsers} />
      )}
    </div>
  );
}
